import type { Field, Form } from '@formily/core';
import { isPlainObject } from 'lodash';

import {
  applyChildCollectionValues,
  buildInitialValuesFromSchemaV2,
} from '../components/form-array-table/utils';
import {
  parseSubSchema,
  type IChildCollection,
} from '../components/form-recursion-item/utils';

type IRow = { id: string } & Record<string, unknown>;

const hasId = (value: unknown): value is IRow =>
  isPlainObject(value) && typeof (value as IRow).id === 'string';

/**
 * `source` replacing `target` in BE update terms: array rows only `target`
 * has come back flagged `toBeDeleted`, keys only `target` has become `null`.
 * Objects carrying an `id` (references, rows) are taken from `source` whole.
 */
export const replaceInstanceValue = (
  target: unknown,
  source: unknown
): unknown => {
  if (Array.isArray(source) || Array.isArray(target)) {
    const sourceRows = Array.isArray(source) ? source : [];
    const sourceIds = new Set(sourceRows.filter(hasId).map(row => row.id));
    const removedRows = (Array.isArray(target) ? target : [])
      .filter(row => hasId(row) && !sourceIds.has(row.id))
      .map(row => ({ ...row, toBeDeleted: true }));
    return [...sourceRows, ...removedRows];
  }

  if (isPlainObject(source) && isPlainObject(target) && !hasId(source)) {
    const targetObject = target as Record<string, unknown>;
    const sourceObject = source as Record<string, unknown>;
    const keys = new Set([
      ...Object.keys(targetObject),
      ...Object.keys(sourceObject),
    ]);
    return Object.fromEntries(
      [...keys].map(key => [
        key,
        key in sourceObject
          ? replaceInstanceValue(targetObject[key], sourceObject[key])
          : null,
      ])
    );
  }

  return source === undefined ? null : source;
};

/** Reshapes table rows the way ArrayTable / RecursionItem do on page load. */
const shapeCollectionRows = (
  field: Field,
  rows: unknown[],
  rootData: Record<string, unknown>
) => {
  const { childSchema, childCollections } = field.componentProps || {};
  const shaped = buildInitialValuesFromSchemaV2(
    rows,
    parseSubSchema(childSchema),
    rootData
  );
  const collections: IChildCollection[] = (childCollections || []).map(
    (collection: IChildCollection) => ({
      ...collection,
      formSchema: parseSubSchema(collection.formSchema),
    })
  );
  return applyChildCollectionValues(shaped, rows, collections, rootData);
};

const markImportedRows = (rows: IRow[], target: unknown) => {
  const targetIds = new Set(
    (Array.isArray(target) ? target : []).filter(hasId).map(row => row.id)
  );
  return rows.map(row =>
    row.toBeDeleted || targetIds.has(row.id)
      ? row
      : { ...row, _formLocal: true }
  );
};

/**
 * Writes `source` instance data over a form loaded from `target`, as values
 * (not initial values) so drafts and Save see the import as changes. Only
 * top-level keys the form has a field for are written; `keep` keys are left.
 */
export const importInstanceIntoForm = (
  form: Form,
  target: Record<string, unknown>,
  source: Record<string, unknown>,
  keep: string[]
) => {
  const replaced = replaceInstanceValue(target, source) as Record<
    string,
    unknown
  >;
  const formKeys = new Set(
    Object.values(form.fields)
      .filter(field => field.displayName !== 'VoidField')
      .map(field => String(field.path.segments[0]))
  );

  formKeys.forEach(key => {
    if (keep.includes(key) || !(key in replaced)) return;
    const value = replaced[key];
    const field = form.query(key).take() as Field | undefined;

    if (Array.isArray(value) && field?.componentProps?.childSchema) {
      const rows = shapeCollectionRows(field, value, replaced) as IRow[];
      form.setValuesIn(key, markImportedRows(rows, target[key]));
      return;
    }
    form.setValuesIn(key, value);
  });
};
